import React from 'react';
import { Box, Alert, AlertTitle, Button } from '@mui/material';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * App-wide error boundary. Without this, any render-time exception in a routed
 * component unmounts the whole app to a blank white screen with no recovery.
 * Wrap the <Routes> in this and key it on the route path so that navigating to
 * another page clears a crashed screen; the Reload button is the hard escape.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // No external error tracking is configured; log to the console so the
    // stack is recoverable from browser devtools.
    console.error('Unhandled error rendering route:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, px: 2 }}>
          <Alert
            severity="error"
            variant="filled"
            sx={{ maxWidth: 560, width: '100%' }}
            action={
              <Button color="inherit" size="small" onClick={this.handleReload}>
                Reload
              </Button>
            }
          >
            <AlertTitle>Something went wrong</AlertTitle>
            This page hit an unexpected error. You can reload to try again, or
            use the menu to go to another page.
          </Alert>
        </Box>
      );
    }
    return this.props.children;
  }
}

export { ErrorBoundary };
export default ErrorBoundary;
